import React, { useCallback, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useSchoolReport } from "../hooks/useSchoolReport";
import ReportDocument from "../components/ReportDocument";

/**
 * Standalone school report — search a Vidyalaya, read its assessment, print it.
 *
 * The same engine answers "complete report for KV X" in the copilot; this
 * surface exists because a report is exported and circulated, which a chat
 * bubble is a poor container for. Everything except the document itself is
 * print:hidden, so what comes off the printer is the report rather than a
 * screenshot of the application.
 */
const SchoolReportPage: React.FC = () => {
  const navigate = useNavigate();
  const { term, setTerm, matches, searching, report, loading, error, openSchool } =
    useSchoolReport();

  // Arriving from the copilot: /analytics/school-report?school=1752 opens the
  // named school immediately, so "open printable report" on a chat answer
  // lands on the finished document rather than an empty search box. Guarded to
  // run once — re-opening on every render would refetch the report each time
  // anything changes.
  /**
   * "Download PDF" is the browser's own print-to-PDF — no PDF library, nothing
   * generated server-side. The one thing the dialog takes from the page is the
   * suggested FILENAME, which comes from document.title; without this the
   * saved file is called after the app instead of the report. Restored on
   * afterprint so the browser tab does not keep the filename as its title.
   */
  const downloadPdf = useCallback(() => {
    if (!report) return;
    const previous = document.title;
    const stamp = new Date().toISOString().slice(0, 10);
    document.title = `KV-${report.school_id}-Assessment-Report-${stamp}`;
    const restore = () => {
      document.title = previous;
      window.removeEventListener("afterprint", restore);
    };
    window.addEventListener("afterprint", restore);
    window.print();
  }, [report]);

  const [params] = useSearchParams();
  const openedFromUrl = useRef(false);
  useEffect(() => {
    const id = Number(params.get("school"));
    if (!openedFromUrl.current && Number.isInteger(id) && id > 0) {
      openedFromUrl.current = true;
      void openSchool(id);
    }
  }, [params, openSchool]);

  return (
    <div className="min-h-screen bg-gray-100 print:bg-white">
      <header
        className="flex items-center gap-3 px-4 h-14 shadow-sm print:hidden"
        style={{ background: "linear-gradient(135deg,#7C3AED 0%,#4F46E5 55%,#2563EB 100%)" }}
      >
        <button
          type="button"
          onClick={() => (window.history.length > 1 ? navigate(-1) : navigate("/dashboard"))}
          aria-label="Go back"
          className="rounded-lg p-1.5 text-white/80 hover:bg-white/15 hover:text-white
            focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60"
        >
          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <div className="flex-1">
          <p className="text-sm font-bold leading-tight text-white">Vidyalaya Assessment Report</p>
          <p className="text-[10px] text-white/65">Infrastructure, ICT, students and staff</p>
        </div>
        {report && (
          <button
            type="button"
            onClick={downloadPdf}
            className="rounded-lg bg-white/10 px-3 py-1.5 text-xs font-semibold text-white/90
              hover:bg-white/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60"
          >
            Download PDF
          </button>
        )}
      </header>

      <main className="mx-auto max-w-4xl px-4 py-6 space-y-4 print:max-w-none print:p-0">
        <div className="relative print:hidden">
          <input
            type="search"
            value={term}
            onChange={e => setTerm(e.target.value)}
            placeholder="Search a Vidyalaya by name, e.g. Mathura Cantt"
            aria-label="Search a Vidyalaya by name"
            className="w-full rounded-xl border border-gray-300 bg-white px-4 py-2.5 text-sm
              outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100"
          />
          {term.trim().length > 0 && term.trim().length < 3 && (
            <p className="mt-1 px-1 text-[11px] text-gray-500">Keep typing — three letters minimum.</p>
          )}
          {matches.length > 0 && (
            <ul className="absolute z-10 mt-1 w-full overflow-hidden rounded-xl border
              border-gray-300 bg-white shadow-lg">
              {matches.map(m => (
                <li key={m.school_id}>
                  <button
                    type="button"
                    onClick={() => openSchool(m.school_id)}
                    className="block w-full px-4 py-2.5 text-left text-sm hover:bg-indigo-50
                      focus:bg-indigo-50 focus:outline-none"
                  >
                    <span className="text-gray-900">{m.school_name}</span>
                    <span className="ml-2 text-xs text-gray-500">{m.region}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
          {!searching && term.trim().length >= 3 && matches.length === 0 && !report && (
            <p className="mt-1 px-1 text-[11px] text-gray-500">No Vidyalaya matches that name.</p>
          )}
        </div>

        {loading && (
          <div className="rounded border border-gray-300 bg-white p-8 text-center text-sm text-gray-600">
            Drawing the report…
          </div>
        )}

        {error && (
          <div className="rounded border border-red-200 bg-red-50 p-4 text-sm text-red-800">
            {error}
          </div>
        )}

        {!report && !loading && !error && (
          <div className="rounded border border-dashed border-gray-400 p-8 text-center print:hidden">
            <p className="text-sm text-gray-700">Search for a Vidyalaya to draw its report.</p>
            <p className="mx-auto mt-1 max-w-md text-xs text-gray-500">
              Every figure is ranked against all active Kendriya Vidyalayas, so each
              observation can be checked against the median printed beside it.
            </p>
          </div>
        )}

        {report && <ReportDocument report={report} />}
      </main>
    </div>
  );
};

export default SchoolReportPage;
